import type { ColumnDef } from '@tanstack/react-table';
import {
  Star, Copy, CheckCircle2, XCircle, AlertCircle, MinusCircle,
  Smile, Meh, Frown, Angry,
} from 'lucide-react';
import { toast } from 'sonner';
import { DataTableColumnHeader } from '@/components/common/data-table';
import {
  Tooltip, TooltipContent, TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatDateTime } from '@/lib/utils/format';
import type { ConversationMetadata } from '@/lib/types/conversations';
import type { TenantRead } from '@harmonyso/tenants-sdk';
import { tenantLabel } from '@/lib/cells/cells';

export interface ConversationsTableMeta {
  tenants?: TenantRead[];
  statistics?: {
    category_distribution?: Record<string, number>;
    workflow_type_distribution?: Record<string, number>;
  };
}

export const createConversationColumns = (
  tenants?: TenantRead[],
  onToggleStar?: (conversationId: string, tenantId: string) => Promise<void>,
): ColumnDef<ConversationMetadata>[] => [
  // Star column - pinned to left
  {
    id: 'star',
    size: 50,
    enableSorting: false,
    enableHiding: false,
    header: () => <div className="flex items-center justify-center"><Star className="h-4 w-4" /></div>,
    cell: ({ row }) => {
      const isStarred = row.original.is_starred;
      const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onToggleStar) {
          void onToggleStar(row.original.id, row.original.tenant_id);
        }
      };
      return (
        <div
          className="flex items-center justify-center cursor-pointer hover:opacity-70 transition-opacity"
          onClick={handleClick}
        >
          <Star
            className={`h-4 w-4 ${isStarred ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`}
          />
        </div>
      );
    },
  },
  // Created
  {
    size: 160,
    accessorKey: 'conversation_date',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Created" />,
    cell: ({ row }) => {
      return (
        <div className="text-sm text-foreground whitespace-nowrap">
          {formatDateTime(row.original.conversation_date)}
        </div>
      );
    },
  },
  // Tenant
  {
    size: 150,
    accessorKey: 'tenant_id',
    enableColumnFilter: true,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Tenant" />,
    cell: ({ row }) => {
      const tenantId = row.original.tenant_id;
      const tenant = tenants?.find(t => t.id === tenantId);
      const tenantName = tenant ? tenantLabel(tenant) : tenantId.slice(0, 8);
      return (
        <div className="text-sm font-medium text-foreground">
          {tenantName}
        </div>
      );
    },
    meta: {
      displayName: 'Tenant',
      getColumnFilters: ({ table }) => {
        const meta = table.options.meta as ConversationsTableMeta | undefined;
        const t = meta?.tenants;
        if (!t) return [];
        return t.map(tenant => ({
          label: tenant.company ? tenantLabel(tenant) : tenant.id.slice(0, 8),
          value: tenant.id,
        }));
      },
    },
  },
  // Title
  {
    size: 400,
    accessorKey: 'title',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Title" />,
    cell: ({ row }) => {
      const title = row.original.title;
      const id = row.original.id;
      return (
        <div className="font-semibold text-foreground max-w-md truncate">
          {title || id.slice(0, 8)}
        </div>
      );
    },
  },
  // Employee
  {
    size: 150,
    accessorKey: 'initiated_by_employee_name',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Employee" />,
    cell: ({ row }) => {
      const employeeName = row.original.initiated_by_employee_name;
      if (!employeeName) return <div className="text-sm text-muted-foreground">-</div>;
      return <div className="text-sm text-foreground">{employeeName}</div>;
    },
  },
  // Last Reply
  {
    size: 160,
    accessorKey: 'last_turn_at',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Last Reply" />,
    cell: ({ row }) => {
      const lastTurn = (row.original as ConversationMetadata & { last_turn_at?: string }).last_turn_at;
      if (!lastTurn) return <div className="text-sm text-muted-foreground">-</div>;
      return (
        <div className="text-sm text-muted-foreground whitespace-nowrap">
          {formatDateTime(lastTurn)}
        </div>
      );
    },
  },
  // Category
  {
    size: 180,
    accessorKey: 'category',
    enableColumnFilter: true,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Category" />,
    cell: ({ row }) => {
      const category = row.original.category;
      const subcategory = row.original.subcategory;
      return (
        <div className="text-sm">
          <div className="font-medium text-foreground">{category || '-'}</div>
          {subcategory && (
            <div className="text-xs text-muted-foreground">{subcategory}</div>
          )}
        </div>
      );
    },
    meta: {
      getColumnFilters: ({ table }) => {
        const meta = table.options.meta as ConversationsTableMeta | undefined;
        const dist = meta?.statistics?.category_distribution;
        if (!dist) return [];
        return Object.keys(dist)
          .sort()
          .map(cat => ({ label: cat, value: cat }));
      },
    },
  },
  {
    size: 180,
    accessorKey: 'ai_category',
    enableColumnFilter: true,
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title="AI Category" />,
    cell: ({ row }) => {
      const aiCategory = row.original.ai_category;
      const aiSubcategory = row.original.ai_subcategory;
      const reason = row.original.category_reasoning;

      const content = (
        <div className="text-sm">
          <div className="font-medium text-foreground">{aiCategory || '-'}</div>
          {aiSubcategory && (
            <div className="text-xs text-muted-foreground">{aiSubcategory}</div>
          )}
        </div>
      );

      if (!reason) return content;

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            {content}
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p className="font-semibold">{aiCategory}</p>
            <p className="mt-1 text-xs opacity-90">
              {reason}
            </p>
          </TooltipContent>
        </Tooltip>
      );
    },
    meta: {
      displayName: 'AI Category',
      getColumnFilters: ({ table }) => {
        const meta = table.options.meta as ConversationsTableMeta | undefined;
        const dist = meta?.statistics?.category_distribution;
        if (!dist) return [];
        return Object.keys(dist)
          .filter(cat => cat !== 'Empty' && cat !== 'Uncategorized')
          .sort()
          .map(cat => ({ label: cat, value: cat }));
      },
    },
  },
  // Sentiment
  {
    size: 120,
    accessorKey: 'sentiment',
    enableColumnFilter: true,
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Sentiment" />,
    cell: ({ row }) => {
      const sentiment = row.original.sentiment;
      if (!sentiment) return <div className="text-sm text-muted-foreground text-center">-</div>;

      const iconMap: Record<string, React.ReactNode> = {
        'Positive': <Smile className="h-5 w-5 text-green-500" />,
        'Neutral': <Meh className="h-5 w-5 text-gray-400" />,
        'Negative': <Frown className="h-5 w-5 text-orange-500" />,
        'Escalating': <Angry className="h-5 w-5 text-red-500" />,
      };

      const reason = row.original.ai_sentiment;
      // eslint-disable-next-line security/detect-object-injection
      const icon = iconMap[sentiment]
        ?? <span className="text-sm">{sentiment}</span>;

      if (!reason) {
        return (
          <div className="flex justify-center cursor-default" title={sentiment}>
            {icon}
          </div>
        );
      }

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex justify-center cursor-default">
              {icon}
            </div>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p className="font-semibold">{sentiment}</p>
            <p className="mt-1 text-xs opacity-90">
              {reason}
            </p>
          </TooltipContent>
        </Tooltip>
      );
    },
    meta: {
      getColumnFilters: () => [
        { label: 'Positive', value: 'Positive' },
        { label: 'Neutral', value: 'Neutral' },
        { label: 'Negative', value: 'Negative' },
        { label: 'Escalating', value: 'Escalating' },
      ],
    },
  },
  // Interest Level
  {
    size: 150,
    accessorKey: 'interest_level',
    enableColumnFilter: true,
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Interest" />,
    cell: ({ row }) => {
      const level = row.original.interest_level;
      if (!level) return <div className="text-sm text-muted-foreground text-center">-</div>;

      const barsMap: Record<string, { count: number; color: string }> = {
        'Not Interesting': { count: 1, color: 'bg-gray-300' },
        'Somewhat Interesting': { count: 2, color: 'bg-blue-400' },
        'Interesting': { count: 3, color: 'bg-yellow-500' },
        'Very Interesting': { count: 4, color: 'bg-red-500' },
      };

      // eslint-disable-next-line security/detect-object-injection
      const config = barsMap[level] ?? { count: 0, color: 'bg-gray-300' };

      const reason = row.original.interest_level_reasoning;
      const bars = (
        <div className="flex items-end justify-center gap-0.5 h-4 w-full px-2">
          {[1, 2, 3, 4].map(i => (
            <div
              key={i}
              className={`w-1 rounded-sm ${i <= config.count ? config.color : 'bg-gray-200'}`}
              style={{ height: `${i * 25}%` }}
            />
          ))}
        </div>
      );

      if (!reason) {
        return <div title={level}>{bars}</div>;
      }

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="w-full cursor-default">{bars}</div>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p className="font-semibold">{level}</p>
            <p className="mt-1 text-xs opacity-90">
              {reason}
            </p>
          </TooltipContent>
        </Tooltip>
      );
    },
    meta: {
      displayName: 'Interest',
      getColumnFilters: () => [
        { label: 'Not Interesting', value: 'Not Interesting' },
        { label: 'Somewhat Interesting', value: 'Somewhat Interesting' },
        { label: 'Interesting', value: 'Interesting' },
        { label: 'Very Interesting', value: 'Very Interesting' },
      ],
    },
  },
  // Resolution
  {
    size: 140,
    accessorKey: 'resolution',
    enableColumnFilter: true,
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Resolution" />,
    cell: ({ row }) => {
      const resolution = row.original.resolution;
      if (!resolution) return <div className="text-sm text-muted-foreground text-center">-</div>;

      const iconMap: Record<string, React.ReactNode> = {
        'Resolved': <CheckCircle2 className="h-5 w-5 text-green-500" />,
        'Partially Resolved': <AlertCircle className="h-5 w-5 text-yellow-500" />,
        'Failed': <XCircle className="h-5 w-5 text-red-500" />,
        'Unresolved': <MinusCircle className="h-5 w-5 text-gray-400" />,
      };

      const reason = row.original.resolution_reasoning;
      // eslint-disable-next-line security/detect-object-injection
      const icon = iconMap[resolution]
        ?? <span className="text-sm">{resolution}</span>;

      if (!reason) {
        return (
          <div className="flex justify-center" title={resolution}>
            {icon}
          </div>
        );
      }

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex justify-center cursor-default">
              {icon}
            </div>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p className="font-semibold">{resolution}</p>
            <p className="mt-1 text-xs opacity-90">
              {reason}
            </p>
          </TooltipContent>
        </Tooltip>
      );
    },
    meta: {
      getColumnFilters: () => [
        { label: 'Resolved', value: 'Resolved' },
        { label: 'Partially Resolved', value: 'Partially Resolved' },
        { label: 'Failed', value: 'Failed' },
        { label: 'Unresolved', value: 'Unresolved' },
      ],
    },
  },
  // Source
  {
    size: 100,
    accessorKey: 'source',
    enableColumnFilter: true,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Source" />,
    cell: ({ row }) => {
      const source = (row.original as ConversationMetadata & { source?: string }).source;
      if (!source) return <div className="text-sm text-muted-foreground">-</div>;

      const colorMap = new Map<string, string>([
        ['slack', 'bg-purple-100 text-purple-700'],
        ['teams', 'bg-blue-100 text-blue-700'],
        ['portal', 'bg-green-100 text-green-700'],
      ]);

      const colorClass = colorMap.get(source) ?? 'bg-gray-100 text-gray-700';

      return (
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
          {source}
        </span>
      );
    },
    meta: {
      getColumnFilters: () => [
        { label: 'Slack', value: 'slack' },
        { label: 'Teams', value: 'teams' },
        { label: 'Portal', value: 'portal' },
      ],
    },
  },
  // App
  {
    size: 150,
    accessorKey: 'app_name',
    enableColumnFilter: true,
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title="App" />,
    cell: ({ row }) => {
      const appName = row.original.app_name;
      if (!appName) return <div className="text-sm text-muted-foreground text-center">-</div>;
      return (
        <div className="text-sm">
          <span className="inline-block px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded">
            {appName}
          </span>
        </div>
      );
    },
  },
  // Messages
  {
    size: 100,
    accessorKey: 'turn_count',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Messages" />,
    cell: ({ row }) => {
      return <div className="text-sm text-foreground text-center">{row.original.turn_count}</div>;
    },
  },
  // Source ID (hidden by default)
  {
    size: 250,
    accessorKey: 'source_identifier',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Source ID" />,
    cell: ({ row }) => {
      const sourceId = (row.original as ConversationMetadata & { source_identifier?: string }).source_identifier;
      if (!sourceId) return <div className="text-sm text-muted-foreground">-</div>;

      const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();
        void navigator.clipboard.writeText(sourceId).then(() => {
          toast.success('Source ID copied to clipboard');
        });
      };

      return (
        <div className="flex items-center gap-2 max-w-[230px]">
          <span className="text-xs font-mono text-foreground truncate" title={sourceId}>
            {sourceId}
          </span>
          <button
            type="button"
            onClick={handleCopy}
            className="shrink-0 p-1 hover:bg-muted rounded transition-colors"
          >
            <Copy className="h-3 w-3 text-muted-foreground" />
          </button>
        </div>
      );
    },
  },
  // Workflows (hidden by default)
  {
    size: 180,
    accessorKey: 'workflow_types',
    enableColumnFilter: true,
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Workflows" />,
    cell: ({ row }) => {
      const workflows = row.original.workflow_types;
      if (!workflows || workflows.length === 0) return <div className="text-sm text-muted-foreground">-</div>;

      return (
        <div className="flex flex-wrap gap-1">
          {workflows.map(wf => (
            <span
              key={wf}
              className="inline-block text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded"
            >
              {wf.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      );
    },
    meta: {
      displayName: 'Workflows',
      getColumnFilters: ({ table }) => {
        const meta = table.options.meta as ConversationsTableMeta | undefined;
        const dist = meta?.statistics?.workflow_type_distribution;
        if (!dist) return [];
        return Object.keys(dist)
          .sort()
          .map(wf => ({
            label: wf.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            value: wf
          }));
      },
    },
  },
];
